import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import cs from 'classnames';
import { Tag } from '@repo/types';
import { NEWS_URL } from '@constants';
import { formatTag } from '../../../utils/formatTag';
import { FeaturedTagsFormData } from './contracts';
import { useFeaturedTagsQuery } from '@data/tags/useFeaturedTagsQuery';

const TAGS_QUERY_PARAM = 'tags';

export type FeaturedTagsProps = FeaturedTagsFormData;

export function FeaturedTags({ featuredTags: featuredTagsFromConfig = [] }: FeaturedTagsProps) {
  const [searchParams] = useSearchParams();
  const tagsQuery = searchParams.get(TAGS_QUERY_PARAM);

  const { data, isLoading, error } = useFeaturedTagsQuery();

  const tags = useMemo(() => {
    return data?.sort((a: Tag, b: Tag) => {
      const aIndex = featuredTagsFromConfig.findIndex((option) => option.value === a.id);
      const bIndex = featuredTagsFromConfig.findIndex((option) => option.value === b.id);

      /**
       * In case the admin has added a new option after booking this component and sorting it's old options
       * then we move the newly added options at the back of the queue
       */
      if (aIndex < 0 || bIndex < 0) {
        return -1;
      }

      return aIndex - bIndex;
    });
  }, [data, featuredTagsFromConfig]);

  const getNewsUrlWithTags = (tag: string) => {
    const currentTagsSet = new Set(tagsQuery ? tagsQuery.split(',') : []);

    if (currentTagsSet.has(tag)) {
      currentTagsSet.delete(tag);
    } else {
      currentTagsSet.add(tag);
    }

    const newTagsQuery = Array.from(currentTagsSet).join(',');

    return `${NEWS_URL}?${newTagsQuery ? `${TAGS_QUERY_PARAM}=${newTagsQuery}` : ''}`;
  };

  const isFeaturedTagSelected = (tag: Tag) => {
    return tagsQuery?.split(',').includes(tag.name);
  };

  const getLinkClassName = (tag: Tag) => {
    return cs('featured-tags__link', 'tag-list__tag', {
      'featured-tags__link--selected': isFeaturedTagSelected(tag),
    });
  };

  if (isLoading || error || !data || !tags) {
    return null;
  }

  return (
    <div className="featured-tags__wrapper">
      <ul className="featured-tags tag-list" id="article-filters">
        {tags.map((tag) => (
          <li className="featured-tags__list-item tag-list__item" key={tag.id}>
            <Link className={getLinkClassName(tag)} to={getNewsUrlWithTags(tag.name)}>
              {formatTag(tag.name)}
            </Link>
          </li>
        ))}
        {tags.length > 0 && (
          <li className="featured-tags__list-item tag-list__item">
            <Link className="featured-tags__link featured-tags__clear-tags tag-list__tag" to={NEWS_URL}>
              <strong>CLEAR ALL</strong>
            </Link>
          </li>
        )}
      </ul>
    </div>
  );
}
